// Transfer Audit Logging System
// Comprehensive logging for order transfers, security events, and admin actions

import { supabase } from './supabase'

export interface AuditLogEntry {
  id?: string
  event_type: 'transfer_created' | 'transfer_claimed' | 'transfer_cancelled' | 'order_status_change' | 'admin_action' | 'security_event' | 'payment_event'
  user_id?: string
  admin_user_id?: string
  order_id?: string
  transfer_token?: string
  old_value?: string
  new_value?: string
  ip_address?: string
  user_agent?: string
  metadata?: Record<string, any>
  severity: 'info' | 'warning' | 'error' | 'critical'
  message: string
  created_at?: string
}

export interface AuditLogFilter {
  eventType?: string
  userId?: string
  orderId?: string
  severity?: string
  startDate?: Date
  endDate?: Date
  limit?: number
  offset?: number
}

// Core audit logging function
export const logAuditEvent = async (entry: Omit<AuditLogEntry, 'id' | 'created_at'>): Promise<boolean> => {
  try {
    // Get client IP and user agent if available
    const clientInfo = getClientInfo()
    
    const auditEntry: AuditLogEntry = {
      ...entry,
      ip_address: entry.ip_address || clientInfo.ip,
      user_agent: entry.user_agent || clientInfo.userAgent,
      created_at: new Date().toISOString()
    }

    const { error } = await supabase
      .from('secret_audit_logs')
      .insert([auditEntry])

    if (error) {
      console.error('Failed to log audit event:', error)
      return false
    }

    // For critical events, also log to external service (placeholder)
    if (entry.severity === 'critical') {
      await logToCriticalMonitoring(auditEntry)
    }

    return true
  } catch (error) {
    console.error('Error logging audit event:', error)
    return false
  }
}

// Specific logging functions for different event types

export const logTransferCreated = async (params: {
  userId: string
  orderId: string
  transferToken: string
  recipientInfo: string
  expiresAt: string
}) => {
  return logAuditEvent({
    event_type: 'transfer_created',
    user_id: params.userId,
    order_id: params.orderId,
    transfer_token: params.transferToken,
    severity: 'info',
    message: `Order transfer created for order ${params.orderId}`,
    metadata: {
      recipient_info: params.recipientInfo,
      expires_at: params.expiresAt,
      action: 'CREATE_TRANSFER'
    }
  })
}

export const logTransferClaimed = async (params: {
  newUserId?: string
  orderId: string
  transferToken: string
  claimedBy: 'existing_user' | 'new_user'
  originalUserId: string
}) => {
  return logAuditEvent({
    event_type: 'transfer_claimed',
    user_id: params.newUserId,
    order_id: params.orderId,
    transfer_token: params.transferToken,
    severity: 'info',
    message: `Order transfer claimed by ${params.claimedBy}`,
    metadata: {
      claimed_by: params.claimedBy,
      original_user_id: params.originalUserId,
      action: 'CLAIM_TRANSFER'
    }
  })
}

export const logTransferCancelled = async (params: {
  userId: string
  orderId: string
  transferToken: string
  reason: 'user_cancelled' | 'expired' | 'admin_cancelled'
}) => {
  return logAuditEvent({
    event_type: 'transfer_cancelled',
    user_id: params.userId,
    order_id: params.orderId,
    transfer_token: params.transferToken,
    severity: 'warning',
    message: `Order transfer cancelled: ${params.reason}`,
    metadata: {
      cancellation_reason: params.reason,
      action: 'CANCEL_TRANSFER'
    }
  })
}

export const logOrderStatusChange = async (params: {
  orderId: string
  userId?: string
  adminUserId?: string
  oldStatus: string
  newStatus: string
  reason?: string
}) => {
  return logAuditEvent({
    event_type: 'order_status_change',
    user_id: params.userId,
    admin_user_id: params.adminUserId,
    order_id: params.orderId,
    old_value: params.oldStatus,
    new_value: params.newStatus,
    severity: 'info',
    message: `Order status changed from ${params.oldStatus} to ${params.newStatus}`,
    metadata: {
      status_change: {
        from: params.oldStatus,
        to: params.newStatus
      },
      reason: params.reason,
      changed_by: params.adminUserId ? 'admin' : 'system',
      action: 'STATUS_CHANGE'
    }
  })
}

export const logAdminAction = async (params: {
  adminUserId: string
  action: string
  targetType: 'order' | 'event' | 'user' | 'system'
  targetId?: string
  description: string
  severity?: 'info' | 'warning' | 'error'
}) => {
  return logAuditEvent({
    event_type: 'admin_action',
    admin_user_id: params.adminUserId,
    order_id: params.targetType === 'order' ? params.targetId : undefined,
    severity: params.severity || 'info',
    message: `Admin action: ${params.description}`,
    metadata: {
      action: params.action,
      target_type: params.targetType,
      target_id: params.targetId,
      admin_action: true
    }
  })
}

export const logSecurityEvent = async (params: {
  eventType: 'suspicious_activity' | 'failed_verification' | 'multiple_attempts' | 'data_breach'
  userId?: string
  description: string
  details: Record<string, any>
}) => {
  return logAuditEvent({
    event_type: 'security_event',
    user_id: params.userId,
    severity: 'critical',
    message: `Security event: ${params.description}`,
    metadata: {
      security_event_type: params.eventType,
      details: params.details,
      requires_investigation: true,
      action: 'SECURITY_ALERT'
    }
  })
}

export const logPaymentEvent = async (params: {
  orderId: string
  userId: string
  paymentId: string
  eventType: 'payment_initiated' | 'payment_completed' | 'payment_failed' | 'refund_processed'
  amount: number
  description: string
}) => {
  return logAuditEvent({
    event_type: 'payment_event',
    user_id: params.userId,
    order_id: params.orderId,
    severity: params.eventType.includes('failed') ? 'error' : 'info',
    message: `Payment event: ${params.description}`,
    metadata: {
      payment_event_type: params.eventType,
      payment_id: params.paymentId,
      amount: params.amount,
      action: 'PAYMENT_EVENT'
    }
  })
}

// Query audit logs with filters
export const getAuditLogs = async (filter: AuditLogFilter = {}) => {
  try {
    let query = supabase
      .from('secret_audit_logs')
      .select(`
        *,
        secret_users!secret_audit_logs_user_id_fkey (
          first_name,
          last_name,
          email,
          phone
        ),
        secret_admin_users!secret_audit_logs_admin_user_id_fkey (
          user_id,
          secret_users!secret_admin_users_user_id_fkey (
            first_name,
            last_name,
            email
          )
        ),
        secret_orders!secret_audit_logs_order_id_fkey (
          order_number,
          total_amount
        )
      `)

    // Apply filters
    if (filter.eventType) {
      query = query.eq('event_type', filter.eventType)
    }
    
    if (filter.userId) {
      query = query.eq('user_id', filter.userId)
    }
    
    if (filter.orderId) {
      query = query.eq('order_id', filter.orderId)
    }
    
    if (filter.severity) {
      query = query.eq('severity', filter.severity)
    }
    
    if (filter.startDate) {
      query = query.gte('created_at', filter.startDate.toISOString())
    }
    
    if (filter.endDate) {
      query = query.lte('created_at', filter.endDate.toISOString())
    }

    // Pagination and ordering
    query = query
      .order('created_at', { ascending: false })
      .limit(filter.limit || 50)
      .offset(filter.offset || 0)

    const { data, error } = await query

    if (error) {
      console.error('Failed to fetch audit logs:', error)
      return { data: [], error }
    }

    return { data: data || [], error: null }
  } catch (error) {
    console.error('Error fetching audit logs:', error)
    return { data: [], error }
  }
}

// Get audit statistics
export const getAuditStats = async (days: number = 30) => {
  try {
    const startDate = new Date()
    startDate.setDate(startDate.getDate() - days)

    const { data, error } = await supabase
      .from('secret_audit_logs')
      .select('event_type, severity, created_at')
      .gte('created_at', startDate.toISOString())

    if (error) {
      console.error('Failed to fetch audit stats:', error)
      return null
    }

    // Process statistics
    const stats = {
      totalEvents: data.length,
      eventsByType: {} as Record<string, number>,
      eventsBySeverity: {} as Record<string, number>,
      dailyActivity: {} as Record<string, number>
    }

    data.forEach(log => {
      // Count by type
      stats.eventsByType[log.event_type] = (stats.eventsByType[log.event_type] || 0) + 1
      
      // Count by severity
      stats.eventsBySeverity[log.severity] = (stats.eventsBySeverity[log.severity] || 0) + 1
      
      // Count by day
      const day = log.created_at.split('T')[0]
      stats.dailyActivity[day] = (stats.dailyActivity[day] || 0) + 1
    })

    return stats
  } catch (error) {
    console.error('Error calculating audit stats:', error)
    return null
  }
}

// Get transfer-specific audit trail
export const getTransferAuditTrail = async (orderId: string) => {
  const { data, error } = await getAuditLogs({
    orderId,
    eventType: undefined // Get all transfer-related events
  })

  if (error) {
    return { data: [], error }
  }

  // Filter for transfer-related events
  const transferEvents = data.filter(log => 
    log.event_type.includes('transfer') || 
    (log.metadata as any)?.action?.includes('TRANSFER')
  )

  return { data: transferEvents, error: null }
}

// Utility functions
const getClientInfo = () => {
  return {
    ip: 'unknown', // Would need server-side implementation
    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : 'unknown'
  }
}

const logToCriticalMonitoring = async (entry: AuditLogEntry) => {
  // Placeholder for external critical event monitoring
  console.warn('CRITICAL AUDIT EVENT:', entry)
  
  // In production, this would send to external monitoring service
  // like DataDog, Sentry, or custom alerting system
}

// Clean up old audit logs (maintenance function)
export const cleanupOldAuditLogs = async (daysToKeep: number = 365) => {
  try {
    const cutoffDate = new Date()
    cutoffDate.setDate(cutoffDate.getDate() - daysToKeep)

    const { data, error } = await supabase
      .from('secret_audit_logs')
      .delete()
      .lt('created_at', cutoffDate.toISOString())
      .select('id')

    if (error) {
      console.error('Failed to cleanup old audit logs:', error)
      return 0
    }

    return data?.length || 0
  } catch (error) {
    console.error('Error cleaning up audit logs:', error)
    return 0
  }
}